import type { WalletTransaction } from '@/api/wallet';

/**
 * Static mapping from Wallet (BudgetBakers) category envelope IDs
 * to ShareCost category keys.
 *
 * Envelope IDs are globally stable across all Wallet users (wallet-android
 * `Envelope.java`). ShareCost keys correspond to the `key` column in the
 * `categories` table.
 *
 * NOTE: This file mirrors supabase/functions/_shared/wallet-categories.ts
 * (the web build can't import from the Deno function tree). Keep both in sync.
 */
export const WALLET_CATEGORY_MAP: Record<number, string> = {
  // Food & Drinks
  1000: 'groceries',
  1001: 'food_and_drinks',
  1002: 'food_and_drinks',
  1003: 'food_and_drinks',

  // Shopping
  2000: 'shopping',
  2001: 'shopping',
  2002: 'health',
  2003: 'shopping',
  2004: 'shopping',
  2005: 'shopping',
  2006: 'shopping',
  2007: 'gifts',
  2008: 'shopping',
  2009: 'entertainment',
  2010: 'shopping',
  2011: 'health',

  // Housing
  3000: 'accommodation',
  3001: 'bills_and_utilities',
  3002: 'bills_and_utilities',
  3003: 'bills_and_utilities',
  3004: 'bills_and_utilities',
  3005: 'bills_and_utilities',
  3010: 'bills_and_utilities',

  // Transportation
  4000: 'transport',
  4001: 'transport',
  4002: 'travel',
  4003: 'transport',
  4004: 'transport',

  // Vehicle
  5000: 'transport',
  5001: 'transport',
  5002: 'transport',
  5003: 'transport',
  5004: 'transport',
  5010: 'bills_and_utilities',
  8006: 'transport',

  // Life & Entertainment
  6000: 'health',
  6001: 'health',
  6002: 'sports',
  6003: 'entertainment',
  6004: 'entertainment',
  6005: 'entertainment',
  6006: 'education',
  6007: 'education',
  6008: 'entertainment',
  6009: 'travel',
  6010: 'gifts',
  6011: 'food_and_drinks',
  6012: 'entertainment',
  6013: 'entertainment',

  // Communication & PC
  7001: 'bills_and_utilities',
  7002: 'bills_and_utilities',
  7003: 'entertainment',
  7004: 'bills_and_utilities',
  7005: 'bills_and_utilities',

  // Financial Expenses
  8000: 'bills_and_utilities',
  8001: 'bills_and_utilities',
  8002: 'bills_and_utilities',
  8003: 'bills_and_utilities',
  8004: 'bills_and_utilities',
  8005: 'bills_and_utilities',
  8007: 'other',
  8008: 'bills_and_utilities',

  // Investments
  9000: 'other',
  9001: 'other',
  9002: 'other',
  9003: 'other',
  9004: 'other',
  9005: 'other',

  // Others / Unknown
  11000: 'other',
  11001: 'other',
  13001: 'other',
};

/**
 * Wallet category group id (`category.group.id`) → the group's generic
 * envelope, used for custom subcategories when no parentId is available.
 */
export const WALLET_GROUP_FALLBACK_ENVELOPE: Record<string, number> = {
  food_and_drinks: 1003,
  shopping: 2010,
  housing: 3005,
  transportation: 4004,
  vehicle: 5004,
  life_entertainment: 6013,
  communication_pc: 7005,
  financial_expenses: 8008,
  investments: 9005,
  others: 11000,
};

/**
 * Wallet API 2.0 dropped `envelopeId` from record categories, but base-category
 * UUIDs are deterministic and encode it: 5c5c<envelope hex4>-<super hex4>-8000-8000-….
 */
const BASE_CATEGORY_ID_PATTERN = /^5c5c([0-9a-f]{4})-[0-9a-f]{4}-8000-8000-000000000000$/i;

export function deriveEnvelopeId(categoryId: string | null | undefined): number | null {
  if (!categoryId) return null;
  const match = BASE_CATEGORY_ID_PATTERN.exec(categoryId);
  return match ? parseInt(match[1], 16) : null;
}

/**
 * Resolve a Wallet envelope ID to a ShareCost category key.
 * Returns undefined for unknown/null envelope IDs.
 */
export function resolveWalletCategory(envelopeId: number | null | undefined): string | undefined {
  if (envelopeId == null) return undefined;
  return WALLET_CATEGORY_MAP[envelopeId];
}

type StagedCategoryFields = Pick<WalletTransaction, 'wallet_category_envelope_id' | 'wallet_category_id' | 'raw_data'>;

/**
 * Resolve the ShareCost category key of a staged Wallet transaction:
 * stored envelope id → parentId (custom subcategory → base) → category id →
 * category group, mirroring the edge functions.
 */
export function resolveWalletCategoryForTransaction(tx: StagedCategoryFields): string | undefined {
  if (tx.wallet_category_envelope_id != null) {
    return resolveWalletCategory(tx.wallet_category_envelope_id);
  }

  const rawCategory = (tx.raw_data as { category?: { parentId?: string; group?: { id?: string } } } | undefined)?.category;
  const derived = deriveEnvelopeId(rawCategory?.parentId) ?? deriveEnvelopeId(tx.wallet_category_id);
  if (derived != null) return resolveWalletCategory(derived);

  const groupId = rawCategory?.group?.id;
  return groupId ? resolveWalletCategory(WALLET_GROUP_FALLBACK_ENVELOPE[groupId]) : undefined;
}
